import { apiClient } from '@app/api';
import { useActiveStation } from '@app/core/stations/hooks/useActiveStation';
import { useYoutubeStatsEnabled } from '@app/modules/audit/hooks/useYoutubeStatsEnabled';
import { date, toTimestamp } from '@mi-front/core';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { stationsEnum } from '../helpers';
import {
  useYoutubeStatsCapture,
  YoutubeStatsRangeLabel,
  YoutubeStatsStationsScope,
} from '../useYoutubeStatsCapture';
import YoutubeStatsView from './YoutubeStatsView';

/**
 * Legacy logger `yt-data` YouTube viewer stats and country channel list.
 */
export function YoutubeStatsContainerV1() {
  const youtubeStatsCapture = useYoutubeStatsCapture();
  const [dateRange, setDateRange] = useState({
    label: '1hr',
    range: {
      from: date().subtract(1, 'hours').toISOString(),
      to: date().toISOString(),
    },
  });

  const todayDate = useMemo(() => date(), [dateRange.range.from, dateRange.range.to]);
  const today = useMemo(() => todayDate.toISOString(), [todayDate]);
  const oneHourAgo = useMemo(
    () => todayDate.clone().subtract(1, 'hours').toISOString(),
    [todayDate],
  );
  const threeHoursAgo = useMemo(
    () => todayDate.clone().subtract(3, 'hours').toISOString(),
    [todayDate],
  );
  const sixHoursAgo = useMemo(
    () => todayDate.clone().subtract(6, 'hours').toISOString(),
    [todayDate],
  );
  const twelveHoursAgo = useMemo(
    () => todayDate.clone().subtract(12, 'hours').toISOString(),
    [todayDate],
  );
  const twentyFourHoursAgo = useMemo(
    () => todayDate.clone().subtract(24, 'hours').toISOString(),
    [todayDate],
  );

  const dateRanges = useMemo(
    () => [
      {
        label: '1hr',
        range: { from: oneHourAgo, to: today },
      },
      {
        label: '3hrs',
        range: { from: threeHoursAgo, to: today },
      },
      {
        label: '6hrs',
        range: { from: sixHoursAgo, to: today },
      },
      {
        label: '12hrs',
        range: { from: twelveHoursAgo, to: today },
      },
      {
        label: '24hrs',
        range: { from: twentyFourHoursAgo, to: today },
      },
    ],
    [oneHourAgo, threeHoursAgo, sixHoursAgo, twelveHoursAgo, twentyFourHoursAgo, today],
  );
  const station = useActiveStation();
  const youtubeStatsEnabled = useYoutubeStatsEnabled(station);
  const [stationsSelection, setStationsSelectionState] = useState(stationsEnum.current);

  const setStationsSelection = useCallback(
    (value: string | null) => {
      if (!value) {
        return;
      }

      youtubeStatsCapture.stationsScopeChanged({ scope: value as YoutubeStatsStationsScope });
      setStationsSelectionState(value);
    },
    [youtubeStatsCapture],
  );

  const { data: youtubeMetadataByCountry, isLoading: isLoadingYoutubeMetadataByCountry } = useQuery(
    {
      queryKey: ['youtube-stats-by-country', station.language.split('-')[1]],
      queryFn: () => apiClient.audit.fetchYoutubeMetadataByCountry(station.language.split('-')[1]),
      enabled: youtubeStatsEnabled,
    },
  );

  const handleDateRangeChange = useCallback(
    (selectedLabel: string) => {
      const selectedDateRange = dateRanges.find((range) => range.label === selectedLabel);
      if (!selectedDateRange) {
        return;
      }

      youtubeStatsCapture.dateRangeChanged({
        range_label: selectedLabel as YoutubeStatsRangeLabel,
      });
      setDateRange(selectedDateRange);
    },
    [dateRanges, youtubeStatsCapture],
  );

  const stationsList = useMemo(() => {
    return youtubeMetadataByCountry?.map((channel: { channelId: string; channelName: string }) => {
      return {
        channelId: channel.channelId,
        channelName: channel.channelName,
      };
    });
  }, [youtubeMetadataByCountry]);

  const [selectedStations, setSelectedStationsState] = useState([station.youtubeChannelId!]);

  const setSelectedStations = useCallback(
    (channelIds: string[]) => {
      youtubeStatsCapture.stationFilterApplied({
        selected_channel_count: channelIds.length,
      });
      setSelectedStationsState(channelIds);
    },
    [youtubeStatsCapture],
  );

  const { data, isLoading } = useQuery({
    queryKey: [
      'youtube-stats-graph',
      dateRange.range.from,
      dateRange.range.to,
      stationsSelection,
      selectedStations,
    ],
    queryFn: () =>
      apiClient.audit.fetchYoutubeStats(
        stationsSelection === stationsEnum.all
          ? youtubeMetadataByCountry
              ?.map((channel: { channelId: string }) => channel.channelId)
              .join(',') || '_all'
          : stationsSelection === stationsEnum.selection
            ? selectedStations.join(',')
            : station.youtubeChannelId || '',
        {
          from: toTimestamp(dateRange.range.from),
          to: toTimestamp(dateRange.range.to),
        },
      ),
    enabled:
      Boolean(youtubeMetadataByCountry) &&
      !isLoadingYoutubeMetadataByCountry &&
      youtubeStatsEnabled,
  });

  return (
    <YoutubeStatsView
      data={data}
      stationsSelection={stationsSelection}
      setStationsSelection={setStationsSelection}
      stationsList={stationsList ?? []}
      selectedStations={selectedStations}
      setSelectedStations={setSelectedStations}
      isLoading={isLoading}
      dateRanges={dateRanges}
      handleDateRangeChange={handleDateRangeChange}
      statusYoutubeMetadataByCountry={youtubeMetadataByCountry ?? []}
      dateRange={dateRange}
    />
  );
}
